import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCachedResource } from '../../hooks/useCachedResource';
import { Link } from 'react-router-dom';
import {
  fetchFulfillmentOrders,
  lookupFulfillmentHawb,
  receiveFulfillmentHawb,
  fetchItlBookingConfig,
  fetchItlCourierRates,
  bookItlPacketsForHawb,
  downloadItlLabelsPdf,
  cancelItlPacket,
} from '../../api/fulfillment';
import { updateI2iTracking, updateOrderSellable, updateFulfillmentRemark } from '../../api/orders';
import { addPriorityOrderId, removePriorityOrderId } from '../customer-service/priorityOrdersStorage';
import ConfirmShipmentModal from '../procurement/ConfirmShipmentModal';
import BulkConfirmShipmentModal from '../procurement/BulkConfirmShipmentModal';
import { useNow } from '../orders/useNow';
import { SellerCentralOrderDate } from '../orders/orderDateDisplay';
import { OrderStatusBadge } from '../orders/orderStatus';
import {
  EmptyState,
  StatusBanner,
  PageLoading,
  ConfirmDialog,
  inputClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
} from '../../components/ui';
import { OrderLineProducts } from '../../components/ui/OrderLineProducts';
import { ProductThumb } from '../../components/ui/ProductThumb';
import { CopyableValue } from '../../components/ui/CopyableValue';

const PAGE_SIZE = 25;

const NON_SELLABLE_REASON_OPTIONS = [
  { value: 'missing_quantity', label: 'Missing quantity' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'swapped', label: 'Swapped' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'return_by_buyer', label: 'Return by buyer' },
];

function normalizeItlServiceType(raw) {
  const value = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (value.includes('air')) return 'air';
  return 'surface';
}

function itlCourierOptionKey(courier) {
  return `${String(courier?.logisticName || '').trim()}::${normalizeItlServiceType(courier?.serviceType)}`;
}

function SurfaceModeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" aria-hidden="true">
      <path
        d="M3 13h11V7H3v6Zm11 0h3.5L20 16.5V13h-6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="17.5" r="1.6" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="17.5" r="1.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 13v3.5h2.2M14 16.5h1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function AirModeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" aria-hidden="true">
      <path
        d="M21 13.5 14 12l-3.5 7-1.7-.7L10.5 12 4 10.5l-.8-1.6L11 10l1.2-5.2L14 5.5 13.2 10.5 21 12l0 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ItlServiceBadge({ serviceType }) {
  const mode = normalizeItlServiceType(serviceType);
  const isAir = mode === 'air';
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        isAir ? 'bg-sky-100 text-sky-800' : 'bg-orange-100 text-orange-800',
      ].join(' ')}
      title={isAir ? 'Air shipping' : 'Surface shipping'}
    >
      {isAir ? <AirModeIcon /> : <SurfaceModeIcon />}
      {isAir ? 'Air' : 'Surface'}
    </span>
  );
}

function formatDeliveryAddress(order) {
  const parts = [
    order.shipAddressLine1,
    order.shipAddressLine2,
    order.shipCity,
    order.shipState,
    order.shipPincode,
  ]
    .map((part) => (part ? String(part).trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : '';
}

function isHawbItlBooked(order) {
  return Boolean(String(order?.i2iTrackingId || '').trim());
}

function isItlBookFailed(row) {
  if (!row?.orderId) return false;
  if (row.waybill || row.i2iSaved || row.skipped) return false;
  const status = String(row.status || '').toLowerCase();
  return (
    status.includes('fail') ||
    status.includes('error') ||
    Boolean(String(row.remark || '').trim())
  );
}

function HawbPacketMenu({ disabled, onCancelRebook }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <div className="relative inline-flex" ref={wrapRef}>
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
        disabled={disabled}
        aria-label="Packet actions"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="flex flex-col items-center justify-center gap-[2px]" aria-hidden="true">
          <span className="h-[3px] w-[3px] rounded-full bg-current" />
          <span className="h-[3px] w-[3px] rounded-full bg-current" />
          <span className="h-[3px] w-[3px] rounded-full bg-current" />
        </span>
      </button>
      {open ? (
        <div className="absolute right-0 top-8 z-30 min-w-[12.5rem] rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onCancelRebook?.();
            }}
          >
            Cancel packet & rebook
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Green "Pushed" after CRM successfully confirmed tracking on Amazon. */
function HawbPushedLabel({ disabled, onCancelRebook }) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-sm font-semibold text-emerald-700">Pushed</span>
      <HawbPacketMenu disabled={disabled} onCancelRebook={onCancelRebook} />
    </div>
  );
}

/** Push button when not yet confirmed via CRM Amazon API. */
function HawbAmazonActions({
  disabled,
  pushedHint,
  onPush,
  onCancelRebook,
  pushLabel = 'Push',
}) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        className={`${secondaryButtonClassName} h-8 px-3 text-xs`}
        disabled={disabled}
        onClick={onPush}
      >
        {pushLabel}
      </button>
      {pushedHint ? (
        <span
          className="text-[11px] font-medium text-amber-700"
          title="Amazon status looks shipped, but CRM has not confirmed this I2I push yet"
        >
          Check SC
        </span>
      ) : null}
      <HawbPacketMenu disabled={disabled} onCancelRebook={onCancelRebook} />
    </div>
  );
}

function bookableHawbOrderIds(orders) {
  return (orders || [])
    .filter((order) => order.orderId && !isHawbItlBooked(order))
    .map((order) => order.orderId);
}

function isEkartName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[\s._-]+/g, '')
    .includes('ekart');
}

function bookedCourierLabel(order, itlResult) {
  const itlRow = (itlResult?.results || []).find(
    (row) => row.orderId === order.orderId && row.waybill && row.logisticName
  );
  if (itlRow?.logisticName) return String(itlRow.logisticName).trim();
  return String(order.courierName || '').trim();
}

function resolveHawbAmazonCarrier(order, itlResult) {
  const booked = bookedCourierLabel(order, itlResult);
  if (!booked || isEkartName(booked)) return 'Other';
  return booked;
}

function hawbAmazonShipTarget(order, itlResult) {
  const bookedCourier = bookedCourierLabel(order, itlResult);
  const carrierCode = resolveHawbAmazonCarrier(order, itlResult);
  return {
    orderId: order.orderId,
    marketplace: order.marketplace || 'IN',
    amazonOrderId: order.amazonOrderId || '',
    trackingNumber: String(order.i2iTrackingId || '').trim(),
    initialShipDate: order.orderDate
      ? new Date(order.orderDate).toISOString().slice(0, 10)
      : '',
    initialCarrierCode: carrierCode,
    carrierCode,
    carrierName: carrierCode === 'Other' ? bookedCourier : undefined,
    bookedCourier,
  };
}

function hawbBulkAmazonTarget(order, itlResult) {
  const target = hawbAmazonShipTarget(order, itlResult);
  return {
    ...target,
    lineId: order.orderId,
  };
}

function getI2iTracking(order) {
  return order.i2iTrackingId || '';
}

/** True only after CRM Amazon push API succeeded (tracking_events / trackingPushed). */
function isCrmAmazonPushConfirmed(order) {
  return Boolean(order?.trackingPushed);
}

function isI2iPushedToAmazon(order) {
  const savedI2i = String(getI2iTracking(order) || '').trim();
  if (!savedI2i) return false;
  if (isCrmAmazonPushConfirmed(order)) return true;
  const amazonTracking = String(order.trackingDummy || '').trim();
  if (amazonTracking && amazonTracking.toLowerCase() === savedI2i.toLowerCase()) return true;
  // Manual Seller Central confirm: Amazon status syncs to shipped/delivered.
  const amazonStatus = String(order.amazonStatus || order.status || '').toLowerCase();
  return (
    amazonStatus === 'shipped' ||
    amazonStatus === 'delivered' ||
    amazonStatus === 'invoiceunconfirmed'
  );
}

function getOrderSellable(order) {
  return order.sellable !== false;
}

function getOrderNonSellableReason(order) {
  return order.nonSellableReason || '';
}

function getFulfillmentRemark(order) {
  return order.fulfillmentRemark || '';
}

const TABS = [
  {
    id: 'Amazon',
    label: 'Amazon',
    accent: 'border-blue-500 text-blue-800 bg-blue-50',
    row: 'bg-blue-50',
  },
  {
    id: 'Inventory',
    label: 'Inventory',
    accent: 'border-emerald-500 text-emerald-800 bg-emerald-50',
    row: 'bg-emerald-50',
  },
  {
    id: 'Not feasible',
    label: 'Not feasible',
    accent: 'border-slate-500 text-slate-800 bg-slate-200/80',
    row: 'bg-slate-200/80',
  },
];

function FulfillmentPage({ token }) {
  const now = useNow();
  const [activeType, setActiveType] = useState('Amazon');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    days: '90',
  });
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [i2iDrafts, setI2iDrafts] = useState({});
  const [savingI2iId, setSavingI2iId] = useState('');
  const [sellableDrafts, setSellableDrafts] = useState({});
  const [savingSellableId, setSavingSellableId] = useState('');
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [savingRemarkId, setSavingRemarkId] = useState('');
  const [hawbInput, setHawbInput] = useState('');
  const [hawbLookup, setHawbLookup] = useState(null);
  const [hawbFinding, setHawbFinding] = useState(false);
  const [hawbReceiving, setHawbReceiving] = useState(false);
  const [hawbConfirmOpen, setHawbConfirmOpen] = useState(false);
  const [itlConfigured, setItlConfigured] = useState(false);
  const [itlBooking, setItlBooking] = useState(false);
  const [itlRatesLoading, setItlRatesLoading] = useState(false);
  const [itlRates, setItlRates] = useState(null);
  const [selectedLogistics, setSelectedLogistics] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('surface');
  const [selectedOrderIds, setSelectedOrderIds] = useState(() => new Set());
  const [itlResult, setItlResult] = useState(null);
  const [itlLabelsLoading, setItlLabelsLoading] = useState(false);
  const [cancelPacketOrder, setCancelPacketOrder] = useState(null);
  const [cancellingPacket, setCancellingPacket] = useState(false);
  const [amazonShipTarget, setAmazonShipTarget] = useState(null);
  const [amazonBulkTargets, setAmazonBulkTargets] = useState(null);
  const [openingAmazonShipId, setOpeningAmazonShipId] = useState('');
  const [addressOrder, setAddressOrder] = useState(null);

  const cacheKey = useMemo(
    () =>
      `fulfillment|${activeType}|${page}|${filters.days || '90'}|${filters.search.trim()}|${PAGE_SIZE}`,
    [activeType, page, filters.days, filters.search]
  );

  const liveItlPollRef = useRef(false);

  const fetcher = useCallback(async () => {
    const params = {
      type: activeType,
      page: String(page),
      pageSize: String(PAGE_SIZE),
      days: filters.days || '90',
    };
    if (filters.search.trim()) params.search = filters.search.trim();
    // Live poll: force fresh ITL track API sync on the server.
    if (liveItlPollRef.current) params.syncItl = '1';
    return fetchFulfillmentOrders(token, params);
  }, [token, activeType, page, filters.days, filters.search]);

  const { data, setData, loading, refreshing, reload, error } = useCachedResource(
    token ? cacheKey : null,
    fetcher,
    { staleTime: 90_000 }
  );

  const orders = data?.orders || [];
  const counts = data?.counts || {
    Amazon: 0,
    Inventory: 0,
    'Not feasible': 0,
  };
  const pagination = data?.pagination || {
    page,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  useEffect(() => {
    if (!data?.orders) return;
    setI2iDrafts(
      Object.fromEntries((data.orders || []).map((order) => [order.id, getI2iTracking(order)]))
    );
    setSellableDrafts(
      Object.fromEntries(
        (data.orders || []).map((order) => [
          order.id,
          {
            sellable: getOrderSellable(order),
            nonSellableReason: getOrderNonSellableReason(order),
          },
        ])
      )
    );
    setRemarkDrafts(
      Object.fromEntries(
        (data.orders || []).map((order) => [order.id, getFulfillmentRemark(order)])
      )
    );
  }, [data]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchItlBookingConfig(token)
      .then((payload) => {
        if (!cancelled) setItlConfigured(Boolean(payload?.configured));
      })
      .catch(() => {
        if (!cancelled) setItlConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (loading && !orders.length) {
      setStatus({ type: 'loading', message: '' });
      return;
    }
    if (error && !orders.length) {
      setStatus({ type: 'error', message: error.message || 'Unable to load fulfillment.' });
      return;
    }
    if (data && status.type === 'loading') {
      setStatus({ type: 'ready', message: '' });
    }
  }, [loading, error, data, orders.length, status.type]);

  const loadOrders = useCallback(
    async ({ force = false, liveItl = false } = {}) => {
      try {
        liveItlPollRef.current = Boolean(liveItl);
        await reload({
          force: force || liveItl,
          silent: Boolean(data?.orders?.length) && !(force && !liveItl),
        });
      } catch {
        // surfaced via status effect
      } finally {
        liveItlPollRef.current = false;
      }
    },
    [reload, data]
  );

  const hasLiveItlTracking = useMemo(
    () => (orders || []).some((order) => Boolean(String(order?.i2iTrackingId || '').trim())),
    [orders]
  );

  // Auto-refresh ITL courier status while Fulfillment is open.
  useEffect(() => {
    if (!token || !hasLiveItlTracking) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadOrders({ liveItl: true });
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [token, hasLiveItlTracking, loadOrders]);

  const handleHawbFind = async (event) => {
    event?.preventDefault?.();
    const hawb = String(hawbInput || '').trim();
    if (!hawb) {
      setStatus({ type: 'error', message: 'Enter the HAWB on the parcel.' });
      return;
    }
    setHawbFinding(true);
    try {
      const payload = await lookupFulfillmentHawb(token, hawb);
      setHawbLookup(payload);
      setItlResult(null);
      setItlRates(null);
      setSelectedLogistics('');
      setSelectedServiceType('surface');
      setSelectedOrderIds(new Set(bookableHawbOrderIds(payload.orders)));
      if (!payload.orders?.length) {
        setStatus({ type: 'error', message: 'No orders found for this HAWB.' });
      } else {
        setStatus({ type: 'ready', message: '' });
      }
    } catch (err) {
      setHawbLookup(null);
      setStatus({ type: 'error', message: err.message });
    } finally {
      setHawbFinding(false);
    }
  };

  const handleHawbReceive = async () => {
    const hawb = String(hawbLookup?.hawb || hawbInput || '').trim();
    if (!hawb || hawbReceiving) return;
    setHawbReceiving(true);
    try {
      const payload = await receiveFulfillmentHawb(token, hawb);
      setHawbLookup(payload);
      setHawbConfirmOpen(false);
      setStatus({
        type: 'success',
        message: payload.message || 'Received. Orders are now in Fulfillment.',
      });
      await loadOrders({ force: true });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setHawbReceiving(false);
    }
  };

  const handleItlRates = async (orderIdsOverride) => {
    const hawb = String(hawbLookup?.hawb || hawbInput || '').trim();
    if (!hawb || itlRatesLoading || itlBooking) return;
    if (!itlConfigured) {
      setStatus({
        type: 'error',
        message:
          'iThink Logistics is not configured. Add ITHINK_* keys and ITHINK_FROM_PINCODE to crm-backend/.env.',
      });
      return;
    }
    const orderIds = Array.isArray(orderIdsOverride) ? orderIdsOverride : [...selectedOrderIds];
    if (!orderIds.length) {
      setStatus({ type: 'error', message: 'Select at least one order to compare rates.' });
      return;
    }
    setItlRatesLoading(true);
    setItlRates(null);
    setSelectedLogistics('');
    setSelectedServiceType('surface');
    try {
      const payload = await fetchItlCourierRates(token, hawb, { force: false, orderIds });
      setItlRates(payload);
      const bookable = (payload?.couriers || []).filter(
        (row) =>
          row.coversAll !== false &&
          Number(row.packetCount || 0) === Number(payload.packetCount || 0)
      );
      const first = bookable[0];
      setSelectedLogistics(first?.logisticName || '');
      setSelectedServiceType(normalizeItlServiceType(first?.serviceType));
      const totalCouriers = payload?.couriers?.length || 0;
      const bookableCount = bookable.length;
      setStatus({
        type: 'success',
        message: totalCouriers
          ? `Found ${totalCouriers} courier option${totalCouriers === 1 ? '' : 's'} (Surface/Air) · ${bookableCount} cover all ${payload.packetCount} remaining packet${payload.packetCount === 1 ? '' : 's'}. Pick one and book.`
          : 'No couriers available for the selected packets on this HAWB.',
      });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setItlRatesLoading(false);
    }
  };

  const handleItlBook = async ({ force = false } = {}) => {
    const hawb = String(hawbLookup?.hawb || hawbInput || '').trim();
    if (!hawb || itlBooking) return;
    if (!itlConfigured) {
      setStatus({
        type: 'error',
        message:
          'iThink Logistics is not configured. Add ITHINK_ACCESS_TOKEN, ITHINK_SECRET_KEY, ITHINK_PICKUP_ADDRESS_ID, and ITHINK_RETURN_ADDRESS_ID to crm-backend/.env.',
      });
      return;
    }
    if (!selectedLogistics) {
      setStatus({
        type: 'error',
        message: 'Get courier rates first, then select a courier to book.',
      });
      return;
    }
    const orderIds = [...selectedOrderIds];
    if (!orderIds.length) {
      setStatus({ type: 'error', message: 'Select at least one order to book.' });
      return;
    }
    setItlBooking(true);
    setItlResult(null);
    try {
      const payload = await bookItlPacketsForHawb(token, hawb, {
        force,
        logistics: selectedLogistics,
        serviceType: selectedServiceType,
        orderIds,
      });
      setItlResult(payload);
      const logisticById = new Map(
        (payload.results || [])
          .filter((row) => row.orderId && row.waybill && row.logisticName)
          .map((row) => [row.orderId, String(row.logisticName).trim()])
      );
      const waybills = new Map(
        (payload.results || [])
          .filter((row) => row.orderId && row.waybill)
          .map((row) => [row.orderId, String(row.waybill).trim()])
      );
      setHawbLookup((current) => {
        if (!current) return current;
        if (!waybills.size) return current;
        const patch = (order) =>
          waybills.has(order.orderId)
            ? {
                ...order,
                i2iTrackingId: waybills.get(order.orderId),
                courierName: logisticById.get(order.orderId) || order.courierName,
              }
            : order;
        return {
          ...current,
          orders: (current.orders || []).map(patch),
          batches: (current.batches || []).map((batch) => ({
            ...batch,
            orders: (batch.orders || []).map(patch),
          })),
        };
      });
      setData((current) => {
        if (!current || !waybills.size) return current;
        return {
          ...current,
          orders: (current.orders || []).map((row) =>
            waybills.has(row.id)
              ? {
                  ...row,
                  i2iTrackingId: waybills.get(row.id) || row.i2iTrackingId,
                  courierName: logisticById.get(row.id) || row.courierName,
                }
              : row
          ),
        };
      });
      const failedIds = (payload.results || [])
        .filter((row) => isItlBookFailed(row))
        .map((row) => row.orderId);
      setSelectedOrderIds(new Set(failedIds));
      setItlRates(null);
      const bookedLabel = selectedLogistics;
      const modeLabel = normalizeItlServiceType(selectedServiceType) === 'air' ? 'Air' : 'Surface';
      setSelectedLogistics('');
      setSelectedServiceType('surface');
      setStatus({
        type: failedIds.length ? 'error' : 'success',
        message: `Booked via ${bookedLabel} (${modeLabel}): ${payload.booked || 0} · I2I saved ${
          payload.savedI2i || 0
        } · skipped ${payload.skipped || 0} · failed ${payload.failed || 0}.${
          failedIds.length
            ? ` ${failedIds.length} remaining — pick another courier for those packets.`
            : ''
        }`,
      });
      await loadOrders({ force: true });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setItlBooking(false);
    }
  };

  const allHawbOrderIds = useMemo(
    () => (hawbLookup?.orders || []).map((order) => order.orderId).filter(Boolean),
    [hawbLookup]
  );
  const bookableHawbIds = useMemo(
    () => bookableHawbOrderIds(hawbLookup?.orders),
    [hawbLookup]
  );
  // ITL-booked packets not yet confirmed via CRM Amazon push.
  const hawbPushableOrders = useMemo(
    () =>
      (hawbLookup?.orders || []).filter(
        (order) => isHawbItlBooked(order) && !isCrmAmazonPushConfirmed(order)
      ),
    [hawbLookup]
  );
  const hawbBookedOrders = useMemo(
    () => (hawbLookup?.orders || []).filter((order) => isHawbItlBooked(order)),
    [hawbLookup]
  );
  const remainingItlFailures = useMemo(
    () =>
      (itlResult?.results || []).filter((row) => {
        if (!isItlBookFailed(row)) return false;
        const order = (hawbLookup?.orders || []).find((item) => item.orderId === row.orderId);
        return !isHawbItlBooked(order);
      }),
    [itlResult, hawbLookup]
  );
  const remainingItlFailureIds = useMemo(
    () => remainingItlFailures.map((row) => row.orderId).filter(Boolean),
    [remainingItlFailures]
  );
  const allOrdersSelected =
    bookableHawbIds.length > 0 && bookableHawbIds.every((id) => selectedOrderIds.has(id));

  const toggleOrderSelected = (orderId) => {
    const order = (hawbLookup?.orders || []).find((row) => row.orderId === orderId);
    if (isHawbItlBooked(order)) return;
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
    setItlRates(null);
    setSelectedLogistics('');
    setSelectedServiceType('surface');
  };

  const selectAllOrders = () => {
    setSelectedOrderIds(new Set(bookableHawbIds));
    setItlRates(null);
    setSelectedLogistics('');
    setSelectedServiceType('surface');
  };

  const clearOrderSelection = () => {
    setSelectedOrderIds(new Set());
    setItlRates(null);
    setSelectedLogistics('');
    setSelectedServiceType('surface');
  };

  const handleRetryRemainingItl = (orderIds = remainingItlFailureIds) => {
    const ids = (orderIds || []).filter(Boolean);
    if (!ids.length) {
      setStatus({ type: 'error', message: 'No remaining failed packets to rebook.' });
      return;
    }
    setSelectedOrderIds(new Set(ids));
    setItlRates(null);
    setSelectedLogistics('');
    setSelectedServiceType('surface');
    handleItlRates(ids);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        if (current.search === searchInput) {
          return current;
        }
        setPage(1);
        return { ...current, search: searchInput };
      });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const selectTab = (type) => {
    setActiveType(type);
    setPage(1);
  };

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleI2iDraftChange = (orderId, value) => {
    setI2iDrafts((current) => ({ ...current, [orderId]: value }));
  };

  const openAmazonShipConfirm = (order, trackingNumber) => {
    const tracking = String(trackingNumber || '').trim();
    if (!tracking) {
      setStatus({ type: 'error', message: 'Enter I2I tracking before confirming on Amazon.' });
      return;
    }
    setAmazonShipTarget({
      orderId: order.id,
      marketplace: order.marketplace || 'IN',
      amazonOrderId: order.amazonOrderId || '',
      trackingNumber: tracking,
      initialShipDate: order.orderDate
        ? new Date(order.orderDate).toISOString().slice(0, 10)
        : '',
      initialCarrierCode: order.courierName || '',
    });
  };

  const handleI2iSave = async (order, { openAmazonConfirm = true } = {}) => {
    const nextValue = String(i2iDrafts[order.id] ?? '').trim();
    const previousValue = getI2iTracking(order);
    if (nextValue === previousValue) {
      setI2iDrafts((current) => ({ ...current, [order.id]: previousValue }));
      if (openAmazonConfirm && nextValue && !isI2iPushedToAmazon(order)) {
        openAmazonShipConfirm(order, nextValue);
      }
      return Boolean(nextValue);
    }

    setSavingI2iId(order.id);
    try {
      const payload = await updateI2iTracking(token, order.id, nextValue);
      const savedValue = payload.order?.i2iTrackingId || '';
      setData((current) => {
        if (!current) return current;
        return {
          ...current,
          orders: (current.orders || []).map((row) =>
            row.id === order.id ? { ...row, i2iTrackingId: savedValue || null } : row
          ),
        };
      });
      setI2iDrafts((current) => ({ ...current, [order.id]: savedValue }));
      setStatus({
        type: 'success',
        message: savedValue
          ? 'I2I saved — pick an Amazon courier to push tracking to Seller Central.'
          : 'I2I tracking cleared.',
      });
      if (openAmazonConfirm && savedValue) {
        openAmazonShipConfirm(
          { ...order, i2iTrackingId: savedValue },
          savedValue
        );
      }
      return Boolean(savedValue);
    } catch (err) {
      setI2iDrafts((current) => ({ ...current, [order.id]: previousValue }));
      setStatus({ type: 'error', message: err.message });
      return false;
    } finally {
      setSavingI2iId('');
    }
  };

  const handleOpenAmazonShip = async (order) => {
    if (openingAmazonShipId || savingI2iId) return;
    setOpeningAmazonShipId(order.id);
    try {
      const tracking = String(i2iDrafts[order.id] ?? getI2iTracking(order) ?? '').trim();
      if (!tracking) {
        setStatus({ type: 'error', message: 'Enter I2I tracking first.' });
        return;
      }
      const saved = await handleI2iSave(order, { openAmazonConfirm: false });
      if (!saved) return;
      openAmazonShipConfirm(order, tracking);
    } finally {
      setOpeningAmazonShipId('');
    }
  };

  const handleAmazonShipSuccess = (payload) => {
    const orderId = amazonShipTarget?.orderId;
    const updated = payload?.order;
    const pushedCourier =
      updated?.courierName ||
      payload?.courierName ||
      null;
    setData((current) => {
      if (!current || !orderId) return current;
      return {
        ...current,
        orders: (current.orders || []).map((row) =>
          row.id === orderId
            ? {
                ...row,
                ...(updated || {}),
                i2iTrackingId:
                  updated?.i2iTrackingId ||
                  amazonShipTarget?.trackingNumber ||
                  row.i2iTrackingId,
                trackingDummy:
                  updated?.trackingDummy ||
                  amazonShipTarget?.trackingNumber ||
                  row.trackingDummy,
                trackingPushed: true,
                courierName: pushedCourier || updated?.courierName || row.courierName,
                status: updated?.status || 'shipped',
              }
            : row
        ),
      };
    });
    if (orderId) {
      markHawbOrdersPushedToAmazon(
        [orderId],
        { [orderId]: amazonShipTarget?.trackingNumber || updated?.trackingDummy }
      );
    }
    setAmazonShipTarget(null);
    setStatus({
      type: 'success',
      message:
        payload?.message ||
        'Tracking pushed to Amazon. CRM marks Delivered from ITL tracking (not Amazon expected date) and then moves it to Customer Service / Orders.',
    });
  };

  const markHawbOrdersPushedToAmazon = (orderIds, trackingById = {}) => {
    const idSet = new Set((orderIds || []).filter(Boolean));
    if (!idSet.size) return;
    setHawbLookup((current) => {
      if (!current) return current;
      const patch = (order) => {
        if (!idSet.has(order.orderId)) return order;
        const tracking = trackingById[order.orderId] || order.i2iTrackingId;
        return {
          ...order,
          trackingPushed: true,
          trackingDummy: tracking || order.trackingDummy,
        };
      };
      return {
        ...current,
        orders: (current.orders || []).map(patch),
        batches: (current.batches || []).map((batch) => ({
          ...batch,
          orders: (batch.orders || []).map(patch),
        })),
      };
    });
    setItlResult((current) => {
      if (!current?.results?.length) return current;
      return {
        ...current,
        results: current.results.map((row) =>
          idSet.has(row.orderId) ? { ...row, amazonPushed: true } : row
        ),
      };
    });
  };

  const handleHawbPushOne = (order) => {
    if (!isHawbItlBooked(order)) return;
    const tracking = String(order.i2iTrackingId || '').trim();
    if (!tracking) {
      setStatus({ type: 'error', message: 'No I2I tracking to push. Book ITL first.' });
      return;
    }
    const carrierCode = resolveHawbAmazonCarrier(order, itlResult);
    openAmazonShipConfirm(
      {
        id: order.orderId,
        marketplace: order.marketplace || 'IN',
        amazonOrderId: order.amazonOrderId || '',
        orderDate: order.orderDate,
        courierName: carrierCode,
      },
      tracking
    );
  };

  const handleHawbPushAll = () => {
    if (!hawbPushableOrders.length) {
      setStatus({ type: 'error', message: 'No booked packets left to push to Amazon.' });
      return;
    }
    setAmazonBulkTargets(hawbPushableOrders.map((order) => hawbBulkAmazonTarget(order, itlResult)));
  };

  const handleHawbCancelPacket = async () => {
    const order = cancelPacketOrder;
    const hawb = String(hawbLookup?.hawb || hawbInput || '').trim();
    if (!order?.orderId || !hawb) {
      setCancelPacketOrder(null);
      return;
    }
    setCancellingPacket(true);
    try {
      await cancelItlPacket(token, hawb, order.orderId);
      setHawbLookup((current) => {
        if (!current) return current;
        const patch = (row) =>
          row.orderId === order.orderId
            ? { ...row, i2iTrackingId: null, trackingPushed: false, courierName: null }
            : row;
        return {
          ...current,
          orders: (current.orders || []).map(patch),
          batches: (current.batches || []).map((batch) => ({
            ...batch,
            orders: (batch.orders || []).map(patch),
          })),
        };
      });
      setItlResult((current) => {
        if (!current?.results?.length) return current;
        return {
          ...current,
          results: current.results.map((row) =>
            row.orderId === order.orderId
              ? {
                  ...row,
                  waybill: null,
                  i2iSaved: false,
                  amazonPushed: false,
                  status: 'Cancelled',
                  remark: 'ITL cancelled',
                }
              : row
          ),
        };
      });
      setSelectedOrderIds(new Set([order.orderId]));
      setCancelPacketOrder(null);
      setStatus({
        type: 'ready',
        message: `Cancelled ITL packet${order.i2iTrackingId ? ` ${order.i2iTrackingId}` : ''}. Compare rates and rebook.`,
      });
      handleItlRates([order.orderId]);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to cancel ITL packet.',
      });
    } finally {
      setCancellingPacket(false);
    }
  };

  const handleDownloadItlLabels = async () => {
    const hawb = String(hawbLookup?.hawb || hawbInput || '').trim();
    if (!hawb) {
      setStatus({ type: 'error', message: 'Find a HAWB first.' });
      return;
    }
    if (!hawbBookedOrders.length) {
      setStatus({ type: 'error', message: 'No ITL-booked packets on this HAWB to print.' });
      return;
    }
    setItlLabelsLoading(true);
    try {
      await downloadItlLabelsPdf(token, hawb);
      setStatus({
        type: 'ready',
        message: `Downloaded ${hawbBookedOrders.length} ITL shipping labels as one PDF.`,
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to download ITL shipping labels.',
      });
    } finally {
      setItlLabelsLoading(false);
    }
  };

  const handleHawbBulkAmazonComplete = (summary) => {
    const succeeded = summary?.succeeded || [];
    if (succeeded.length) {
      const trackingById = Object.fromEntries(
        succeeded.map((row) => [
          row.orderId,
          row.payload?.order?.trackingDummy ||
            row.payload?.trackingNumber ||
            (hawbLookup?.orders || []).find((order) => order.orderId === row.orderId)?.i2iTrackingId,
        ])
      );
      markHawbOrdersPushedToAmazon(
        succeeded.map((row) => row.orderId),
        trackingById
      );
      setData((current) => {
        if (!current) return current;
        const idSet = new Set(succeeded.map((row) => row.orderId));
        return {
          ...current,
          orders: (current.orders || []).map((row) =>
            idSet.has(row.id)
              ? {
                  ...row,
                  trackingPushed: true,
                  trackingDummy: trackingById[row.id] || row.trackingDummy,
                  i2iTrackingId: trackingById[row.id] || row.i2iTrackingId,
                  status: 'shipped',
                }
              : row
          ),
        };
      });
    }
    const failedCount = summary?.failed?.length || 0;
    setStatus({
      type: failedCount ? 'error' : 'success',
      message: `Pushed ${succeeded.length} I2I tracking${succeeded.length === 1 ? '' : 's'} to Amazon${
        failedCount ? ` · ${failedCount} failed` : ''
      }.`,
    });
  };

  const getSellableDraft = (order) => {
    const draft = sellableDrafts[order.id];
    if (draft) return draft;
    return {
      sellable: getOrderSellable(order),
      nonSellableReason: getOrderNonSellableReason(order),
    };
  };

  const patchSellableDraft = (orderId, patch) => {
    setSellableDrafts((current) => {
      const prev = current[orderId] || { sellable: true, nonSellableReason: '' };
      return { ...current, [orderId]: { ...prev, ...patch } };
    });
  };

  const handleSellableSave = async (order, next) => {
    const previous = {
      sellable: getOrderSellable(order),
      nonSellableReason: getOrderNonSellableReason(order),
    };
    if (
      Boolean(next.sellable) === previous.sellable &&
      String(next.nonSellableReason || '') === String(previous.nonSellableReason || '')
    ) {
      return;
    }

    if (!next.sellable && !String(next.nonSellableReason || '').trim()) {
      setStatus({
        type: 'error',
        message: 'Select a reason to mark as non-sellable.',
      });
      return;
    }

    setSavingSellableId(order.id);
    try {
      const payload = await updateOrderSellable(token, order.id, {
        sellable: next.sellable,
        nonSellableReason: next.sellable ? null : next.nonSellableReason,
      });
      const savedSellable = payload.order?.sellable !== false;
      const savedReason = payload.order?.nonSellableReason || '';
      const savedFulfillmentRemark = payload.order?.fulfillmentRemark || '';
      setData((current) => {
        if (!current) return current;
        return {
          ...current,
          orders: (current.orders || []).map((row) =>
            row.id === order.id
              ? {
                  ...row,
                  sellable: savedSellable,
                  nonSellableReason: savedReason || null,
                  fulfillmentRemark: savedFulfillmentRemark || null,
                }
              : row
          ),
        };
      });
      setSellableDrafts((current) => ({
        ...current,
        [order.id]: { sellable: savedSellable, nonSellableReason: savedReason },
      }));
      setRemarkDrafts((current) => ({
        ...current,
        [order.id]: savedFulfillmentRemark,
      }));

      const previousWasPriorityReason =
        previous.nonSellableReason === 'damaged' ||
        previous.nonSellableReason === 'missing_quantity';
      const nowPriorityReason =
        !savedSellable &&
        (savedReason === 'damaged' || savedReason === 'missing_quantity');
      if (nowPriorityReason) {
        addPriorityOrderId(order.id);
      } else if (previousWasPriorityReason) {
        removePriorityOrderId(order.id);
      }

      setStatus({
        type: 'success',
        message: savedSellable ? 'Marked sellable.' : 'Marked non-sellable.',
      });
    } catch (err) {
      setSellableDrafts((current) => ({
        ...current,
        [order.id]: previous,
      }));
      setStatus({ type: 'error', message: err.message });
    } finally {
      setSavingSellableId('');
    }
  };

  const handleSellableToggle = (order, sellable) => {
    if (sellable) {
      patchSellableDraft(order.id, { sellable: true, nonSellableReason: '' });
      handleSellableSave(order, { sellable: true, nonSellableReason: null });
      return;
    }

    const existingReason = String(getSellableDraft(order).nonSellableReason || '').trim();
    if (!existingReason) {
      // Show reason dropdown, but do not persist until a reason is chosen.
      patchSellableDraft(order.id, { sellable: false, nonSellableReason: '' });
      setStatus({
        type: 'error',
        message: 'Select a reason to mark as non-sellable.',
      });
      return;
    }

    patchSellableDraft(order.id, {
      sellable: false,
      nonSellableReason: existingReason,
    });
    handleSellableSave(order, { sellable: false, nonSellableReason: existingReason });
  };

  const handleReasonChange = (order, reason) => {
    const nextReason = String(reason || '').trim();
    if (!nextReason) {
      // Reason is mandatory for non-sellable — revert empty selection.
      const previousReason = getOrderNonSellableReason(order);
      if (previousReason) {
        patchSellableDraft(order.id, {
          sellable: false,
          nonSellableReason: previousReason,
        });
      } else {
        patchSellableDraft(order.id, { sellable: true, nonSellableReason: '' });
      }
      setStatus({
        type: 'error',
        message: 'Select a reason to mark as non-sellable.',
      });
      return;
    }

    patchSellableDraft(order.id, { sellable: false, nonSellableReason: nextReason });
    handleSellableSave(order, { sellable: false, nonSellableReason: nextReason });
  };

  const handleRemarkDraftChange = (orderId, value) => {
    setRemarkDrafts((current) => ({ ...current, [orderId]: value }));
  };

  const handleRemarkSave = async (order) => {
    const nextValue = String(remarkDrafts[order.id] ?? '').trim();
    const previousValue = getFulfillmentRemark(order);
    if (nextValue === previousValue) {
      setRemarkDrafts((current) => ({ ...current, [order.id]: previousValue }));
      return;
    }

    setSavingRemarkId(order.id);
    try {
      const payload = await updateFulfillmentRemark(token, order.id, nextValue);
      const savedValue = payload.order?.fulfillmentRemark || '';
      setData((current) => {
        if (!current) return current;
        return {
          ...current,
          orders: (current.orders || []).map((row) =>
            row.id === order.id
              ? { ...row, fulfillmentRemark: savedValue || null }
              : row
          ),
        };
      });
      setRemarkDrafts((current) => ({ ...current, [order.id]: savedValue }));
      setStatus({ type: 'success', message: 'Remark saved.' });
    } catch (err) {
      setRemarkDrafts((current) => ({ ...current, [order.id]: previousValue }));
      setStatus({ type: 'error', message: err.message });
    } finally {
      setSavingRemarkId('');
    }
  };

  const activeTab = TABS.find((tab) => tab.id === activeType) || TABS[0];
  const showSellableColumns = activeType === 'Amazon';
  const total = pagination.total || 0;
  const totalPages = pagination.totalPages || 0;
  const rangeStart = total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, total);

  return (
    <section className="grid gap-4">
      <StatusBanner status={status} setStatus={setStatus} />

      <article className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Receive HAWB</h2>
            <p className="mt-1 text-sm text-slate-500">Enter the HAWB on the parcel.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasLiveItlTracking ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800"
                title="ITL statuses refresh from iThink every minute while this page is open, and every 5 minutes in the background."
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                Live ITL tracking
              </span>
            ) : null}
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => loadOrders({ force: true, liveItl: true })}
              disabled={loading || refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <form className="mt-3 flex flex-wrap gap-2" onSubmit={handleHawbFind}>
          <input
            className={`${inputClassName} max-w-sm font-mono`}
            type="text"
            value={hawbInput}
            onChange={(event) => setHawbInput(event.target.value)}
            placeholder="HAWB number"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            className={primaryButtonClassName}
            disabled={hawbFinding || hawbReceiving}
          >
            {hawbFinding ? 'Finding…' : 'Find'}
          </button>
        </form>

        {hawbLookup ? (
          <div className="mt-4 grid gap-3">
            <p className="text-sm text-slate-600">
              {(() => {
                const batchCount = hawbLookup.batches?.length || (hawbLookup.orders.length ? 1 : 0);
                const batchLabel = `${batchCount} batch${batchCount === 1 ? '' : 'es'}`;
                const orderLabel = `${hawbLookup.orders.length} order${hawbLookup.orders.length === 1 ? '' : 's'} on this HAWB`;
                return hawbLookup.alreadyReceived
                  ? `${orderLabel} · ${batchLabel} · Already received`
                  : `${orderLabel} · ${batchLabel} · ${hawbLookup.pendingOrderCount} ready to receive`;
              })()}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {!hawbLookup.alreadyReceived ? (
                <button
                  type="button"
                  className={primaryButtonClassName}
                  disabled={
                    hawbReceiving ||
                    hawbFinding ||
                    itlBooking ||
                    itlRatesLoading ||
                    !hawbLookup.orders.length
                  }
                  onClick={() => setHawbConfirmOpen(true)}
                >
                  Mark received
                </button>
              ) : null}
              {hawbLookup.alreadyReceived && (bookableHawbIds.length || remainingItlFailures.length) ? (
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={hawbReceiving || hawbFinding || itlBooking || itlRatesLoading}
                  onClick={() => {
                    if (selectedOrderIds.size) {
                      handleItlRates();
                      return;
                    }
                    if (remainingItlFailureIds.length) {
                      handleRetryRemainingItl();
                      return;
                    }
                    handleItlRates(bookableHawbIds);
                  }}
                  title={
                    itlConfigured
                      ? 'Compare ITL courier rates for selected or remaining packets'
                      : 'Configure ITHINK_* env vars first'
                  }
                >
                  {itlRatesLoading
                    ? 'Loading rates…'
                    : remainingItlFailures.length && !selectedOrderIds.size
                      ? `Compare rates for remaining (${remainingItlFailures.length})`
                      : 'Compare courier rates'}
                </button>
              ) : null}
              {hawbLookup.alreadyReceived && selectedLogistics ? (
                <button
                  type="button"
                  className={primaryButtonClassName}
                  disabled={
                    hawbReceiving ||
                    hawbFinding ||
                    itlBooking ||
                    itlRatesLoading ||
                    !selectedOrderIds.size
                  }
                  onClick={() => handleItlBook({ force: false })}
                >
                  {itlBooking
                    ? `Booking ${selectedLogistics} (${normalizeItlServiceType(selectedServiceType) === 'air' ? 'Air' : 'Surface'})…`
                    : `Book ${selectedLogistics} (${normalizeItlServiceType(selectedServiceType) === 'air' ? 'Air' : 'Surface'}) + paste I2I`}
                </button>
              ) : null}
              {hawbBookedOrders.length ? (
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  onClick={handleDownloadItlLabels}
                  disabled={itlLabelsLoading}
                >
                  {itlLabelsLoading
                    ? 'Downloading labels…'
                    : `Download ITL labels (${hawbBookedOrders.length})`}
                </button>
              ) : null}
              {hawbPushableOrders.length ? (
                <button
                  type="button"
                  className={primaryButtonClassName}
                  onClick={handleHawbPushAll}
                >
                  {`Push all I2I to Amazon (${hawbPushableOrders.length})`}
                </button>
              ) : null}
            </div>

            {itlRates?.couriers?.length ? (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Couriers for {itlRates.packetCount} packet
                  {itlRates.packetCount === 1 ? '' : 's'} · from pin {itlRates.fromPincode}
                  {itlRates.skippedWithI2i
                    ? ` · ${itlRates.skippedWithI2i} already have I2I (skipped)`
                    : ''}
                  {' · '}
                  Book only couriers that cover every selected packet
                </div>
                <table className="w-full min-w-[36rem] border-collapse text-sm">
                  <thead>
                    <tr className="bg-white">
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Select
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Courier
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Mode
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Coverage
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Total price
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Avg / packet
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        TAT (days)
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Zone
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itlRates.couriers.map((courier) => {
                      const coversAll = courier.coversAll !== false
                        && Number(courier.packetCount || 0) === Number(itlRates.packetCount || 0);
                      const optionKey = itlCourierOptionKey(courier);
                      const selected =
                        selectedLogistics === courier.logisticName &&
                        normalizeItlServiceType(selectedServiceType) ===
                          normalizeItlServiceType(courier.serviceType);
                      const fullCoverageIndex = itlRates.couriers
                        .filter((row) => row.coversAll !== false
                          && Number(row.packetCount || 0) === Number(itlRates.packetCount || 0))
                        .findIndex((row) => itlCourierOptionKey(row) === optionKey);
                      const selectCourier = () => {
                        if (!coversAll) return;
                        setSelectedLogistics(courier.logisticName);
                        setSelectedServiceType(normalizeItlServiceType(courier.serviceType));
                      };
                      return (
                        <tr
                          key={optionKey}
                          className={[
                            'border-t border-slate-100',
                            coversAll ? 'cursor-pointer' : 'opacity-60',
                            selected ? 'bg-blue-50' : coversAll ? 'hover:bg-slate-50' : '',
                          ].join(' ')}
                          onClick={selectCourier}
                          title={
                            coversAll
                              ? undefined
                              : `Only covers ${courier.packetCount}/${itlRates.packetCount} packets — select fewer orders or pick another courier`
                          }
                        >
                          <td className={tableCellClassName}>
                            <input
                              type="radio"
                              name="itl-courier"
                              checked={selected}
                              disabled={!coversAll}
                              onChange={selectCourier}
                            />
                          </td>
                          <td className={tableCellClassName}>
                            <span className="font-semibold text-slate-900">{courier.logisticName}</span>
                            {coversAll && fullCoverageIndex === 0 ? (
                              <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                                Cheapest
                              </span>
                            ) : null}
                          </td>
                          <td className={tableCellClassName}>
                            <ItlServiceBadge serviceType={courier.serviceType} />
                          </td>
                          <td className={tableCellClassName}>
                            {coversAll ? (
                              <span className="text-emerald-700">All {itlRates.packetCount}</span>
                            ) : (
                              <span className="text-amber-700">
                                {courier.packetCount}/{itlRates.packetCount}
                              </span>
                            )}
                          </td>
                          <td className={tableCellClassName}>
                            ₹{Number(courier.totalRate || 0).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className={tableCellClassName}>
                            ₹{Number(courier.avgRate || 0).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className={tableCellClassName}>{courier.deliveryTat || '—'}</td>
                          <td className={tableCellClassName}>{courier.zone || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {itlResult?.results?.length ? (
              <div className="grid gap-2">
                {remainingItlFailures.length ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                    {remainingItlFailures.length} packet
                    {remainingItlFailures.length === 1 ? '' : 's'} failed
                    {itlResult.logistics ? ` with ${itlResult.logistics}` : ''}
                    {remainingItlFailures.some((row) =>
                      /pincode/i.test(String(row.remark || ''))
                    )
                      ? ' (pincode not serviceable)'
                      : ''}
                    . Use Compare rates above, or Rebook on the row.
                  </div>
                ) : null}
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[40rem] border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Order
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Status
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        I2I / Waybill
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Note
                      </th>
                      <th className={`${tableCellClassName} text-[11px] font-semibold uppercase text-slate-500`}>
                        Amazon
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itlResult.results.map((row) => (
                      <tr
                        key={row.orderId}
                        className={`border-t border-slate-100 ${
                          remainingItlFailures.some((item) => item.orderId === row.orderId)
                            ? 'bg-amber-50'
                            : ''
                        }`}
                      >
                        <td className={tableCellClassName}>
                          <span className="font-mono text-xs">{row.amazonOrderId || row.orderId}</span>
                          {row.buyerName ? (
                            <span className="mt-0.5 block text-xs text-slate-500">{row.buyerName}</span>
                          ) : null}
                        </td>
                        <td className={tableCellClassName}>{row.status}</td>
                        <td className={tableCellClassName}>
                          {row.waybill ? (
                            <CopyableValue value={row.waybill} className="font-mono text-xs" />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={tableCellClassName}>
                          {row.i2iSaved ? 'I2I pasted' : row.remark || '—'}
                        </td>
                        <td className={tableCellClassName}>
                          {(() => {
                            const hawbOrder = (hawbLookup.orders || []).find(
                              (order) => order.orderId === row.orderId
                            );
                            const failed = remainingItlFailures.some(
                              (item) => item.orderId === row.orderId
                            );
                            const crmPushed =
                              Boolean(row.amazonPushed) ||
                              (hawbOrder ? isCrmAmazonPushConfirmed(hawbOrder) : false);
                            const pushedHint =
                              !crmPushed &&
                              (hawbOrder ? isI2iPushedToAmazon(hawbOrder) : false);
                            if (failed) {
                              return (
                                <button
                                  type="button"
                                  className={`${secondaryButtonClassName} h-8 px-3 text-xs`}
                                  disabled={itlRatesLoading || itlBooking}
                                  onClick={() => handleRetryRemainingItl([row.orderId])}
                                >
                                  Rebook
                                </button>
                              );
                            }
                            if (!row.waybill) {
                              return <span className="text-slate-400">—</span>;
                            }
                            if (crmPushed) {
                              return (
                                <HawbPushedLabel
                                  disabled={cancellingPacket || itlBooking}
                                  onCancelRebook={() =>
                                    setCancelPacketOrder(
                                      hawbOrder || {
                                        orderId: row.orderId,
                                        i2iTrackingId: row.waybill,
                                      }
                                    )
                                  }
                                />
                              );
                            }
                            return (
                              <HawbAmazonActions
                                disabled={!hawbOrder || cancellingPacket || itlBooking}
                                pushedHint={pushedHint}
                                onPush={() => hawbOrder && handleHawbPushOne(hawbOrder)}
                                onCancelRebook={() =>
                                  setCancelPacketOrder(
                                    hawbOrder || {
                                      orderId: row.orderId,
                                      i2iTrackingId: row.waybill,
                                    }
                                  )
                                }
                              />
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            ) : null}

            {hawbLookup.orders.length ? (
              <div className="grid gap-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-600">
                    {selectedOrderIds.size} of {bookableHawbIds.length} selected for ITL
                    {allHawbOrderIds.length > bookableHawbIds.length
                      ? ` · ${allHawbOrderIds.length - bookableHawbIds.length} already booked`
                      : ''}
                  </span>
                  {bookableHawbIds.length ? (
                    <>
                      <button
                        type="button"
                        className={secondaryButtonClassName}
                        onClick={selectAllOrders}
                        disabled={allOrdersSelected}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className={secondaryButtonClassName}
                        onClick={clearOrderSelection}
                        disabled={!selectedOrderIds.size}
                      >
                        Clear
                      </button>
                    </>
                  ) : null}
                </div>
                {(hawbLookup.batches?.length ? hawbLookup.batches : [{
                  batchId: 'all',
                  batchName: 'Procurement batch',
                  status: '',
                  createdAt: null,
                  orderCount: hawbLookup.orders.length,
                  pendingOrderCount: hawbLookup.pendingOrderCount,
                  alreadyReceived: hawbLookup.alreadyReceived,
                  orders: hawbLookup.orders,
                }]).map((batch) => (
                  <div key={batch.batchId} className="overflow-hidden rounded-lg border border-slate-200">
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{batch.batchName}</p>
                        <p className="text-[11px] text-slate-500">
                          {[
                            batch.status ? batch.status : null,
                            batch.createdAt
                              ? new Date(batch.createdAt).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : null,
                            `${batch.orderCount} order${batch.orderCount === 1 ? '' : 's'}`,
                            batch.alreadyReceived
                              ? 'Already received'
                              : `${batch.pendingOrderCount} ready`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[58rem] border-collapse">
                        <thead>
                          <tr className="bg-white">
                            <th className={`${tableCellClassName} w-12 text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              <span className="sr-only">Select</span>
                              <input
                                type="checkbox"
                                checked={
                                  batch.orders.filter((order) => order.orderId && !isHawbItlBooked(order)).length > 0 &&
                                  batch.orders
                                    .filter((order) => order.orderId && !isHawbItlBooked(order))
                                    .every((order) => selectedOrderIds.has(order.orderId))
                                }
                                onChange={(event) => {
                                  setSelectedOrderIds((current) => {
                                    const next = new Set(current);
                                    for (const order of batch.orders) {
                                      if (!order.orderId || isHawbItlBooked(order)) continue;
                                      if (event.target.checked) next.add(order.orderId);
                                      else next.delete(order.orderId);
                                    }
                                    return next;
                                  });
                                  setItlRates(null);
                                  setSelectedLogistics('');
                                  setSelectedServiceType('surface');
                                }}
                                aria-label={`Select all in ${batch.batchName}`}
                              />
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Order ID
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Product
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Customer
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Tracking
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Type
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Status
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Address
                            </th>
                            <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                              Amazon
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {batch.orders.map((order) => (
                            <tr key={`${batch.batchId}-${order.orderId}`} className="border-t border-slate-100">
                              <td className={tableCellClassName}>
                                <input
                                  type="checkbox"
                                  checked={selectedOrderIds.has(order.orderId)}
                                  disabled={isHawbItlBooked(order)}
                                  onChange={() => toggleOrderSelected(order.orderId)}
                                  aria-label={`Select ${order.amazonOrderId}`}
                                />
                              </td>
                              <td className={`${tableCellClassName} font-mono text-xs font-semibold`}>
                                {order.amazonOrderId}
                              </td>
                              <td className={tableCellClassName}>
                                <div className="flex items-start gap-3">
                                  <ProductThumb
                                    item={{
                                      title: order.title,
                                      sku: order.sku,
                                      asinIn: order.asinIn || (order.marketplace === 'IN' ? order.asin : null),
                                      asinCom: order.asinCom || (order.marketplace === 'US' ? order.asin : null),
                                      imageUrl: order.imageUrl,
                                    }}
                                    marketplace={order.marketplace || 'IN'}
                                    token={token}
                                    className="h-12 w-12"
                                  />
                                  <div className="min-w-0">
                                    <span className="block text-sm text-slate-800">{order.title || '—'}</span>
                                    {order.sku ? (
                                      <span className="mt-0.5 block font-mono text-[11px] text-slate-500">
                                        {order.sku}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                              <td className={tableCellClassName}>{order.buyerName || '—'}</td>
                              <td className={tableCellClassName}>
                                {order.i2iTrackingId ? (
                                  <CopyableValue value={order.i2iTrackingId} className="font-mono text-xs" />
                                ) : order.usTrackingId ? (
                                  <CopyableValue value={order.usTrackingId} className="font-mono text-xs" />
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                                {order.usTrackingId && order.i2iTrackingId ? (
                                  <span className="mt-0.5 block font-mono text-[11px] text-slate-500">
                                    US {order.usTrackingId}
                                  </span>
                                ) : null}
                                {order.trackingDummy &&
                                order.trackingDummy !== order.usTrackingId &&
                                order.trackingDummy !== order.i2iTrackingId ? (
                                  <span className="mt-0.5 block font-mono text-[11px] text-slate-500">
                                    Dummy {order.trackingDummy}
                                  </span>
                                ) : null}
                              </td>
                              <td className={tableCellClassName}>{order.type}</td>
                              <td className={tableCellClassName}>
                                {(() => {
                                  if (isHawbItlBooked(order)) {
                                    return (
                                      <div className="flex flex-col gap-0.5">
                                        <span className="text-xs font-semibold text-sky-800">
                                          {order.itlStatus || 'ITL booked'}
                                        </span>
                                        {order.courierName ? (
                                          <span className="text-[11px] text-slate-500">
                                            {order.courierName}
                                          </span>
                                        ) : null}
                                      </div>
                                    );
                                  }
                                  const failed = remainingItlFailures.find(
                                    (row) => row.orderId === order.orderId
                                  );
                                  if (failed) return failed.remark || 'ITL failed';
                                  return order.received ? 'Already received' : 'Ready';
                                })()}
                              </td>
                              <td className={tableCellClassName}>
                                <button
                                  type="button"
                                  className={`${secondaryButtonClassName} h-8 px-3 text-xs`}
                                  onClick={() => setAddressOrder(order)}
                                >
                                  View
                                </button>
                              </td>
                              <td className={tableCellClassName}>
                                {!isHawbItlBooked(order) ? (
                                  <span className="text-slate-400">—</span>
                                ) : isCrmAmazonPushConfirmed(order) ? (
                                  <HawbPushedLabel
                                    disabled={cancellingPacket || itlBooking}
                                    onCancelRebook={() => setCancelPacketOrder(order)}
                                  />
                                ) : (
                                  <HawbAmazonActions
                                    disabled={cancellingPacket || itlBooking}
                                    pushedHint={isI2iPushedToAmazon(order)}
                                    onPush={() => handleHawbPushOne(order)}
                                    onCancelRebook={() => setCancelPacketOrder(order)}
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No orders found for this HAWB.</p>
            )}
          </div>
        ) : null}
      </article>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const selected = tab.id === activeType;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={[
                'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition',
                selected
                  ? tab.accent
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              ].join(' ')}
            >
              {tab.label}
              <span
                className={[
                  'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  selected ? 'bg-white/70' : 'bg-slate-100 text-slate-600',
                ].join(' ')}
              >
                {(counts[tab.id] || 0).toLocaleString('en-IN')}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        <label className="grid gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Date range
          </span>
          <select
            className={inputClassName}
            value={filters.days}
            onChange={(event) => updateFilter('days', event.target.value)}
          >
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 180 days</option>
            <option value="all">All time</option>
          </select>
        </label>
        <label className="grid gap-1.5 md:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Search
          </span>
          <input
            className={inputClassName}
            type="search"
            placeholder="HAWB, Order ID, buyer, city, product, ASIN, SKU..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
      </div>

      {loading && !orders.length ? (
        <PageLoading label="Loading fulfillment orders…" />
      ) : orders.length ? (
        <div className="grid gap-3">
          <div className="overflow-x-auto rounded-[10px] border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[84rem] border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Order date
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Product
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Customer
                  </th>
                  <th className="min-w-[12rem] border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Delivery address
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    HAWB
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    I2I tracking
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Carrier
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    ITL status
                  </th>
                  {showSellableColumns ? (
                    <>
                      <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Sellable
                      </th>
                      <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Reason
                      </th>
                    </>
                  ) : null}
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Remark
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Expected delivery
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Total
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const address = formatDeliveryAddress(order);
                  const i2iDraft =
                    i2iDrafts[order.id] !== undefined
                      ? i2iDrafts[order.id]
                      : getI2iTracking(order);
                  const sellableDraft = getSellableDraft(order);
                  const isSellable = sellableDraft.sellable !== false;
                  const needsNonSellableReason =
                    !isSellable && !String(sellableDraft.nonSellableReason || '').trim();
                  const remarkDraft =
                    remarkDrafts[order.id] !== undefined
                      ? remarkDrafts[order.id]
                      : getFulfillmentRemark(order);
                  return (
                  <tr key={order.id} className={activeTab.row}>
                    <td className={tableCellClassName}>
                      <SellerCentralOrderDate
                        value={order.orderDate}
                        marketplace={order.marketplace}
                        now={now}
                      />
                    </td>
                    <td className={tableCellClassName}>
                      <OrderLineProducts order={order} showOrderId />
                    </td>
                    <td className={tableCellClassName}>
                      <span className="text-sm font-medium text-slate-800">
                        {order.buyerName || '—'}
                      </span>
                      {order.buyerPhone ? (
                        <span className="mt-0.5 block text-xs text-slate-500">{order.buyerPhone}</span>
                      ) : null}
                    </td>
                    <td className={tableCellClassName}>
                      <span className="line-clamp-3 text-sm text-slate-700" title={address || undefined}>
                        {address || '—'}
                      </span>
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      {order.hawbNumber ? (
                        <CopyableValue value={order.hawbNumber} className="font-mono text-xs" />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      {(() => {
                        const savedI2i = String(getI2iTracking(order) || '').trim();
                        const i2iFixed = isI2iPushedToAmazon(order);
                        if (i2iFixed) {
                          return (
                            <CopyableValue
                              value={savedI2i}
                              className="font-mono text-xs text-slate-800"
                            />
                          );
                        }
                        return (
                          <div className="flex min-w-[11rem] flex-col gap-1">
                            <input
                              type="text"
                              placeholder="I2I tracking"
                              className={`${inputClassName} w-36 font-mono text-xs`}
                              value={i2iDraft}
                              disabled={savingI2iId === order.id}
                              onChange={(event) =>
                                handleI2iDraftChange(order.id, event.target.value)
                              }
                              onBlur={() => handleI2iSave(order)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') event.currentTarget.blur();
                              }}
                            />
                            {String(i2iDraft || savedI2i).trim() ? (
                              <button
                                type="button"
                                className="text-left text-[11px] font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                                disabled={
                                  savingI2iId === order.id ||
                                  openingAmazonShipId === order.id
                                }
                                onClick={() => handleOpenAmazonShip(order)}
                              >
                                {openingAmazonShipId === order.id
                                  ? 'Opening…'
                                  : 'Confirm on Amazon'}
                              </button>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className={tableCellClassName}>
                      {order.courierName ? (
                        <span className="text-sm font-medium text-slate-800">
                          {order.courierName}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className={tableCellClassName}>
                      {order.itlStatus || isHawbItlBooked(order) ? (
                        <div className="flex min-w-[7rem] flex-col gap-0.5">
                          <span
                            title={
                              order.itlLastScanRemark ||
                              (order.itlStatusCode
                                ? `ITL code ${order.itlStatusCode}`
                                : 'ITL tracking')
                            }
                            className={[
                              'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                              String(order.itlStatus || '')
                                .toLowerCase()
                                .includes('deliver') &&
                              !String(order.itlStatus || '')
                                .toLowerCase()
                                .includes('rto')
                                ? 'bg-emerald-50 text-emerald-800'
                                : String(order.itlStatus || '')
                                      .toLowerCase()
                                      .includes('cancel')
                                  ? 'bg-rose-50 text-rose-800'
                                  : String(order.itlStatus || '')
                                        .toLowerCase()
                                        .includes('rto')
                                    ? 'bg-amber-50 text-amber-900'
                                    : 'bg-sky-50 text-sky-800',
                            ].join(' ')}
                          >
                            {order.itlStatus || 'Awaiting scan'}
                          </span>
                          {order.itlLastScanRemark ? (
                            <span className="max-w-[12rem] truncate text-[10px] text-slate-500">
                              {order.itlLastScanRemark}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    {showSellableColumns ? (
                      <>
                        <td className={`${tableCellClassName} whitespace-nowrap`}>
                          <label className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={isSellable}
                              disabled={savingSellableId === order.id}
                              onClick={() => handleSellableToggle(order, !isSellable)}
                              className={[
                                'relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-60',
                                isSellable ? 'bg-emerald-500' : 'bg-slate-300',
                              ].join(' ')}
                            >
                              <span
                                className={[
                                  'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                                  isSellable ? 'translate-x-5' : 'translate-x-0',
                                ].join(' ')}
                              />
                            </button>
                            <span className="text-xs font-semibold text-slate-700">
                              {isSellable ? 'Sellable' : 'Non-sellable'}
                            </span>
                          </label>
                        </td>
                        <td className={`${tableCellClassName} whitespace-nowrap`}>
                          {isSellable ? (
                            <span className="text-sm text-slate-400">—</span>
                          ) : (
                            <div className="flex min-w-[10rem] flex-col gap-1">
                              <select
                                aria-required="true"
                                aria-invalid={needsNonSellableReason}
                                className={[
                                  `${inputClassName} min-w-[10rem] text-xs`,
                                  needsNonSellableReason
                                    ? 'border-rose-400 ring-1 ring-rose-300 focus:border-rose-500 focus:ring-rose-400'
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                value={sellableDraft.nonSellableReason || ''}
                                disabled={savingSellableId === order.id}
                                onChange={(event) =>
                                  handleReasonChange(order, event.target.value)
                                }
                              >
                                <option value="">Select reason</option>
                                {NON_SELLABLE_REASON_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {needsNonSellableReason ? (
                                <span className="text-[11px] font-medium text-rose-600">
                                  Reason required
                                </span>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </>
                    ) : null}
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      <input
                        type="text"
                        placeholder="Remark"
                        className={`${inputClassName} min-w-[10rem] text-xs`}
                        value={remarkDraft}
                        disabled={savingRemarkId === order.id}
                        onChange={(event) =>
                          handleRemarkDraftChange(order.id, event.target.value)
                        }
                        onBlur={() => handleRemarkSave(order)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur();
                        }}
                      />
                    </td>
                    <td className={tableCellClassName}>
                      <span className="text-sm text-slate-700">
                        {order.itlExpectedDelivery
                          ? String(order.itlExpectedDelivery).slice(0, 10)
                          : order.deliveryDate
                            ? new Date(order.deliveryDate).toLocaleDateString('en-IN')
                            : '—'}
                      </span>
                    </td>
                    <td className={tableCellClassName}>
                      <OrderStatusBadge order={order} />
                    </td>
                    <td className={tableCellClassName}>
                      {order.currency} {Number(order.totalAmount).toFixed(2)}
                    </td>
                    <td className={tableCellClassName}>
                      <Link
                        to={`/admin/orders/${order.id}`}
                        className="font-semibold text-blue-700 hover:text-blue-800"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
            <p className="text-slate-600">
              Showing {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')} order
              {total === 1 ? '' : 's'}
              {filters.days !== 'all' ? ` (last ${filters.days} days)` : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={page <= 1 || loading || refreshing}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="min-w-[7rem] text-center font-medium text-slate-800">
                Page {totalPages === 0 ? 0 : page} of {totalPages}
              </span>
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={totalPages === 0 || page >= totalPages || loading || refreshing}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState
          title={`No ${activeType} fulfillment orders`}
          description={
            filters.days !== 'all'
              ? `No received ${activeType} orders in the last ${filters.days} days. Receive a HAWB above, or try a wider date range.`
              : `No received ${activeType} orders yet. Enter a HAWB above and mark it received.`
          }
          action={
            <Link to="/admin/procurement" className={secondaryButtonClassName}>
              Open Procurement
            </Link>
          }
        />
      )}
      {addressOrder && typeof document !== 'undefined'
        ? createPortal(
        <div
          className="fixed inset-0 z-[220] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
          onClick={() => setAddressOrder(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="fulfillment-address-title"
          >
            <h3 id="fulfillment-address-title" className="m-0 text-base font-semibold text-slate-900">
              Delivery address
            </h3>
            <p className="mt-1 font-mono text-xs font-semibold text-slate-500">
              {addressOrder.amazonOrderId}
            </p>
            <dl className="mt-4 grid gap-2 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Customer</dt>
                <dd className="mt-0.5 m-0 font-medium text-slate-800">{addressOrder.buyerName || '—'}</dd>
              </div>
              {addressOrder.buyerPhone ? (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Phone</dt>
                  <dd className="mt-0.5 m-0 text-slate-700">{addressOrder.buyerPhone}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Address</dt>
                <dd className="mt-0.5 m-0 whitespace-pre-wrap text-slate-800">
                  {formatDeliveryAddress(addressOrder) || 'No address on this order.'}
                </dd>
              </div>
            </dl>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={() => setAddressOrder(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
        : null}

      <ConfirmDialog
        open={hawbConfirmOpen}
        title="Do you want to receive this HAWB?"
        message={
          hawbLookup
            ? `${hawbLookup.hawb} — ${hawbLookup.pendingOrderCount} order${hawbLookup.pendingOrderCount === 1 ? '' : 's'} will move to Fulfillment.`
            : ''
        }
        confirmLabel="Mark received"
        cancelLabel="Cancel"
        confirming={hawbReceiving}
        confirmingLabel="Receiving…"
        onConfirm={handleHawbReceive}
        onCancel={() => {
          if (!hawbReceiving) setHawbConfirmOpen(false);
        }}
      />

      <ConfirmDialog
        open={Boolean(cancelPacketOrder)}
        title="Cancel this ITL packet?"
        message={
          cancelPacketOrder
            ? `This cancels ${cancelPacketOrder.i2iTrackingId || 'the ITL waybill'} on iThink and clears I2I so you can rebook. Amazon tracking stays until you push the new waybill.`
            : ''
        }
        confirmLabel="Cancel packet & rebook"
        cancelLabel="Keep packet"
        confirming={cancellingPacket}
        confirmingLabel="Cancelling…"
        onConfirm={handleHawbCancelPacket}
        onCancel={() => {
          if (!cancellingPacket) setCancelPacketOrder(null);
        }}
      />

      <ConfirmShipmentModal
        isOpen={Boolean(amazonShipTarget)}
        onClose={() => setAmazonShipTarget(null)}
        onSuccess={handleAmazonShipSuccess}
        token={token}
        orderId={amazonShipTarget?.orderId}
        marketplace={amazonShipTarget?.marketplace || 'IN'}
        trackingNumber={amazonShipTarget?.trackingNumber || ''}
        amazonOrderId={amazonShipTarget?.amazonOrderId}
        initialShipDate={amazonShipTarget?.initialShipDate || ''}
        initialCarrierCode={amazonShipTarget?.initialCarrierCode || ''}
        title="Confirm I2I on Amazon"
        trackingHint="Updates tracking + carrier only. Original Amazon ship date is kept."
        submitLabel="Push to Amazon"
        hideShipDate
      />
      <BulkConfirmShipmentModal
        isOpen={Boolean(amazonBulkTargets?.length)}
        onClose={() => setAmazonBulkTargets(null)}
        token={token}
        marketplace={amazonBulkTargets?.[0]?.marketplace || 'IN'}
        targets={amazonBulkTargets || []}
        hideShipDate
        perOrderCarrier
        title="Push all I2I to Amazon"
        submitLabel={`Push ${amazonBulkTargets?.length || 0} to Amazon`}
        continueOnClose
        onProgress={({ done, total }) => {
          setStatus({
            type: 'ready',
            message: `Pushing I2I to Amazon ${done}/${total}… you can close this box, it keeps running.`,
          });
        }}
        onComplete={(summary) => {
          handleHawbBulkAmazonComplete(summary);
          if (!summary?.failed?.length) {
            setAmazonBulkTargets(null);
          }
        }}
      />
    </section>
  );
}

export default FulfillmentPage;
